'use client'

import { motion, AnimatePresence } from 'framer-motion'
import {
  Home,
  Briefcase,
  FileText,
  Calculator,
  FileBarChart,
  ChevronDown,
  PenLine,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { usePageSave } from '../../contexts/PageSaveContext'
import { useServerPageStatus } from '../../contexts/ServerPageStatusContext'

export default function Sidebar({ isOpen, isMobile, onClose }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { isPageSaved, areAllPagesSaved } = usePageSave()
  const { isPageValid, areAllRequiredPagesValid, isSourceOfIncomeValid } = useServerPageStatus()

  const [expandedItem, setExpandedItem] = useState('')
  const [hovered, setHovered] = useState(false)
  const [keepOpen, setKeepOpen] = useState(() => {
    const stored = localStorage.getItem('sidebarKeepOpen')
    return stored ? stored === 'true' : false
  })

  useEffect(() => {
    localStorage.setItem('sidebarKeepOpen', keepOpen)
    try { window.dispatchEvent(new Event('sidebarKeepOpenChanged')) } catch (e) {}
  }, [keepOpen])

  const menuItems = [
    { name: 'Dashboard', icon: Home, link: '/dashboard', pageKey: 'dashboard' },
    {
      name: 'Source of Income',
      icon: Briefcase,
      pageKey: 'source-of-income',
      subItems: [
        { name: 'Salary', link: '/dashboard/salary', pageKey: 'salary' },
        { name: 'House Property', link: '/dashboard/house-property', pageKey: 'house-property' },
        { name: 'Capital Gain', link: '/dashboard/capital-gain', pageKey: 'capital-gain' },
        { name: 'Other Sources', link: '/dashboard/other-sources', pageKey: 'other-sources' }
      ]
    },
    { name: 'Deduction VI-A', icon: Calculator, link: '/dashboard/deduction-vi-a', pageKey: 'deduction-vi-a' },
    { name: 'Exempt Income', icon: FileText, link: '/dashboard/exempt-income', pageKey: 'exempt-income' },
    { name: 'Tax Paid', icon: FileBarChart, link: '/dashboard/tds-tcs', pageKey: 'tds-tcs' },
    { name: 'Filing Section', icon: PenLine, link: '/dashboard/filing-section', pageKey: 'filing-section', isMargin: true }
  ]

  // Auto-expand parent if URL matches a subItem
  useEffect(() => {
    menuItems.forEach((item) => {
      if (item.subItems) {
        const match = item.subItems.find((sub) => sub.link === location.pathname)
        if (match) {
          setExpandedItem(item.name)
        }
      }
    })
  }, [location.pathname])

  const isActive = (item) => {
    if (item.link && location.pathname === item.link) return true
    if (item.subItems) {
      return item.subItems.some((sub) => sub.link === location.pathname)
    }
    return false
  }

  // Function to determine if a page should show green dot based on server data ONLY
  const shouldShowGreenDot = (item) => {
    // Only use server-based validation - no localStorage fallback
    if (item.pageKey === 'dashboard') {
      // Dashboard is green when all required pages have valid data on server
      return areAllRequiredPagesValid()
    } else if (item.pageKey === 'source-of-income') {
      // Source of Income is green when ANY sub-item has valid data on server
      return isSourceOfIncomeValid()
    } else {
      // Regular pages use their server-based validation status
      return isPageValid(item.pageKey)
    }
  }

  // Sidebar open state logic
  let sidebarOpen
  if (isMobile) {
    sidebarOpen = isOpen
  } else {
    sidebarOpen = keepOpen || hovered
  }

  // Sidebar width for collapsed/expanded
  const collapsedWidth = '3.5rem'
  const expandedWidth = '16rem'

  // Split menu items for layout
  const mainItems = menuItems.filter((i) => !i.isMargin)
  const bottomItem = menuItems.find((i) => i.isMargin)

  return (
    <motion.aside
      id="sidebar"
      initial={false}
      animate={sidebarOpen ? 'open' : 'closed'}
      variants={{
        open: { width: expandedWidth, transition: { type: 'spring', stiffness: 300, damping: 30 } },
        closed: { width: collapsedWidth, transition: { type: 'spring', stiffness: 300, damping: 30 } }
      }}
      className="fixed left-0 bottom-0 z-20 bg-white shadow-lg overflow-y-auto flex flex-col border-r border-gray-200"
      style={{top: '0px', overflowX: 'hidden', height: '100vh'}}
      onMouseEnter={() => { if (!keepOpen && !isMobile) setHovered(true) }}
      onMouseLeave={() => { if (!keepOpen && !isMobile) setHovered(false) }}
    >

      {/* Menu */}
      <nav className="flex-1 pt-24 pb-6 flex flex-col">
        
        <ul className="space-y-1">
          {mainItems.map((item) => (
            <li key={item.name}>
              <div
                onClick={() => {
                  if (item.subItems) {
                    setExpandedItem((prev) => (prev === item.name ? '' : item.name))
                  } else {
                    navigate(item.link)
                  }
                }}
                  className={`cursor-pointer flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors mx-2 ${isActive(item)
                  ? 'bg-gray-100 text-cyan-600'
                  : 'hover:bg-gray-100 text-gray-700'
                }`}
                style={{ minHeight: '2.5rem' }}
              >
                <div className={`flex items-center w-full transition-all duration-300 ${sidebarOpen ? 'justify-start gap-2' : 'justify-center'}`}>
                  <item.icon className="w-5 h-5" />
                    {item.pageKey && sidebarOpen && (
                      <div className="flex items-center justify-center w-4">
                        <div className={`w-2 h-2 rounded-full border ${shouldShowGreenDot(item) ? 'bg-green-500' : 'border-gray-400'}`}></div>
                      </div>
                    )}
                    {sidebarOpen && <span className="whitespace-nowrap flex-1">{item.name}</span>}
                </div>
                  <div className="flex items-center gap-2 ml-auto">
                {item.subItems && sidebarOpen && (
                  <ChevronDown
                    className={`w-4 h-4 transform transition-transform ${expandedItem === item.name ? 'rotate-180' : ''}`}
                  />
                )}
                  </div>
              </div>

              {/* Sub Items */}
              <AnimatePresence>
                {item.subItems && expandedItem === item.name && sidebarOpen && (
                  <motion.ul
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="ml-8 mt-1 space-y-1 overflow-hidden pr-8"
                  >
                    {item.subItems.map((sub) => (
                      <li key={sub.name}>
                        <button
                          onClick={() => navigate(sub.link)}
                          className={`w-full text-left px-3 py-2 text-sm rounded-md transition-all duration-200 flex items-center ${
                            location.pathname.startsWith(sub.link)
                              ? ' text-cyan-600 bg-gray-100 font-semibold'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {sub.pageKey && (
                            <div className="flex items-center justify-center w-4 mr-2">
                              <div className={`w-2 h-2 rounded-full border ${isPageValid(sub.pageKey) ? 'bg-green-500' : 'border-gray-400'}`}></div>
                            </div>
                          )}
                          <span className="flex-1">{sub.name}</span>
                        </button>
                      </li>
                    ))}
                  </motion.ul>
                )}
              </AnimatePresence>
            </li>
          ))}
        </ul>


        {bottomItem && (
          <>
            <div className="mt-10"></div>
            <div className="mx-2 my-6 border-t border-gray-200" />
            <ul className="space-y-1">
              <li key={bottomItem.name}>
                <div
                  onClick={() => {
                    if (bottomItem.subItems) {
                      setExpandedItem((prev) => (prev === bottomItem.name ? '' : bottomItem.name))
                    } else {
                      navigate(bottomItem.link)
                    }
                  }}
                  className={`cursor-pointer flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors mx-2 ${isActive(bottomItem)
                    ? 'bg-gray-100 text-cyan-600'
                    : 'hover:bg-gray-100 text-gray-700'
                  }`}
                  style={{ minHeight: '2.5rem' }}
                >
                  <div className={`flex items-center w-full transition-all duration-300 ${sidebarOpen ? 'justify-start gap-2' : 'justify-center'}`}>
                    <bottomItem.icon className="w-5 h-5" />
                    {sidebarOpen && <span className="whitespace-nowrap flex-1">{bottomItem.name}</span>}
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {bottomItem.pageKey && sidebarOpen && (
                      <div className="flex items-center justify-center w-4">
                        <div className={`w-2 h-2 rounded-full border ${isPageValid(bottomItem.pageKey) ? 'bg-green-500' : 'border-gray-400'}`}></div>
                      </div>
                    )}
                  {bottomItem.subItems && sidebarOpen && (
                    <ChevronDown
                      className={`w-4 h-4 transform transition-transform ${expandedItem === bottomItem.name ? 'rotate-180' : ''}`}
                    />
                  )}
                  </div>
                </div>

                <AnimatePresence>
                  {bottomItem.subItems && expandedItem === bottomItem.name && sidebarOpen && (
                    <motion.ul
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="ml-8 mt-1 space-y-1 overflow-hidden"
                    >
                      {bottomItem.subItems.map((sub) => (
                        <li key={sub.name}>
                          <button
                            onClick={() => navigate(sub.link)}
                            className={`w-full text-left px-3 py-2 text-sm rounded-md transition-all duration-200 flex items-center gap-2 ${
                              location.pathname.startsWith(sub.link)
                                ? ' text-cyan-600 bg-gray-100 font-semibold'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            {sub.name}
                          </button>
                        </li>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </li>
            </ul>
          </>
        )}
      </nav>

      {/* Auto-collapse toggle - Fixed position in middle of sidebar */}
          <button
        className={`w-8 h-16 rounded-l-lg border-2 transition-all duration-200 shadow-xl flex items-center justify-center fixed ${
          keepOpen 
            ? 'bg-cyan-500 border-cyan-300 hover:bg-cyan-600' 
            : 'bg-gray-500 border-gray-300 hover:bg-gray-600'
        }`}
        style={{
          left: sidebarOpen ? '16rem' : '3.5rem', // Position based on sidebar width
          top: 'calc(50% + 3rem)', // Adjust for the pt-24 padding (6rem = 96px, so 3rem = 48px offset)
          transform: 'translateY(-50%)',
          zIndex: 60,
          backgroundColor: keepOpen ? '#06b6d4' : '#6b7280',
          borderColor: keepOpen ? '#67e8f9' : '#9ca3af'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = keepOpen ? '#0891b2' : '#4b5563'}
        onMouseLeave={(e) => e.target.style.backgroundColor = keepOpen ? '#06b6d4' : '#6b7280'}
            onClick={() => setKeepOpen((prev) => !prev)}
          >
        <svg 
          className="w-4 h-4 text-white transition-transform duration-200" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
          style={{ transform: sidebarOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
          </button>

    </motion.aside>
  )
}
